import React, { useState, useEffect } from 'react';
import {Modal, Form, Input, Button, Table, Checkbox, Tooltip} from 'antd';
import Swal from 'sweetalert2';
import Inputmask from 'inputmask';
import Flatpickr from 'react-flatpickr';
import { Russian } from 'flatpickr/dist/l10n/ru';
import 'flatpickr/dist/flatpickr.css';
import { enqueueSnackbar } from 'notistack';
import { InfoCircleOutlined, CheckCircleOutlined, CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";

const UserFormModal = ({ visible, onClose, onCreateSuccess, onUpdateSuccess, userToEdit, currentUserIsSuperuser, currentUserIsStaff}) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [issuers, setIssuers] = useState({});
    const [selectedMeetingIds, setSelectedMeetingIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'meeting_date', direction: 'desc' });
    const pageSize = 10;

    const phoneInputRef = React.useRef(null);
    const snInputRef = React.useRef(null);
    const isStaff = Form.useWatch('is_staff', form);

    // --- Загрузка собраний ---
    useEffect(() => {
        const loadMeetings = async () => {
            const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
            try {
                const response = await fetch(`${API_ADDRESS}get_all_meetings/`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setMeetings(data.meetings || []);
                    const issuerMap = {};
                    data.issuers?.forEach(issuer => {
                        issuerMap[issuer.issuer_id] = issuer.full_name;
                    });
                    setIssuers(issuerMap);
                } else {
                    enqueueSnackbar('Не удалось загрузить собрания', { variant: 'error' });
                }
            } catch (error) {
                console.error('Ошибка загрузки собраний:', error);
                enqueueSnackbar('Ошибка сети', { variant: 'error' });
            }
        };

        if (isAssignModalVisible) {
            loadMeetings();
        }
    }, [isAssignModalVisible]);

    // --- Восстановление данных при открытии формы ---
    useEffect(() => {
        if (!visible) return;

        // Маска для телефона: 7 (999) 999-99-99
        if (phoneInputRef.current) {
          Inputmask({
            mask: '7 999 999 99 99',
            showMaskOnHover: false,
            placeholder: '7 ___ ___ __ __',
            clearIncomplete: true,
            onBeforePaste: (pastedValue) => {
              let cleaned = pastedValue.replace(/\D/g, '');
              if (cleaned.startsWith('8')) cleaned = '7' + cleaned.slice(1);
              if (cleaned.startsWith('7')) cleaned = cleaned.slice(1);
              return cleaned.length > 10 ? cleaned.slice(0, 10) : cleaned;
            },
            onKeyDown: (e, input, opts, mask) => {
              if (!/[\dBackspaceDeleteArrow]/.test(e.key)) {
                e.preventDefault();
              }
              const digits = input.inputmask.unmaskedvalue();
              if (digits.length >= 10 && !['Backspace', 'Delete'].includes(e.key) && !e.key.includes('Arrow')) {
                e.preventDefault();
              }
            },
            onBeforeMask: (value, opts) => {
              let digits = value.replace(/\D/g, '');
              if (digits.startsWith('8') && digits.length === 11) {
                digits = '7' + digits.slice(1);
              }
              if (digits.startsWith('7')) {
                digits = digits.slice(1);
              }
              return digits.length > 10 ? digits.slice(0, 10) : digits;
            },
          }).mask(phoneInputRef.current);
        }

        // Маска для паспорта: 9999 999999
        if (snInputRef.current) {
          Inputmask({
            mask: '9999 999999',
            showMaskOnHover: false,
            placeholder: '____ ______',
            clearIncomplete: true,
            onBeforePaste: (pastedValue) => {
              const cleaned = pastedValue.replace(/\D/g, '');
              return cleaned.length > 10 ? cleaned.slice(0, 10) : cleaned;
            },
            onKeyDown: (e, input, opts, mask) => {
              if (!/[\dBackspaceDeleteArrow]/.test(e.key)) {
                e.preventDefault();
              }
              const digits = input.inputmask.unmaskedvalue();
              if (digits.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && !e.key.includes('Arrow')) {
                e.preventDefault();
              }
            },
            onBeforeMask: (value, opts) => {
              const digits = value.replace(/\D/g, '');
              return digits.length > 10 ? digits.slice(0, 10) : digits;
            },
          }).mask(snInputRef.current);
        }
      }, [visible]);

    const handleModalClose = () => {
        form.resetFields();
        setSelectedMeetingIds([]);
        onClose?.();
    };

    // --- Открытие модального окна выбора собраний ---
    const openAssignModal = () => {
        if (!isStaff) {
            enqueueSnackbar('Сначала отметьте "Администратор"', { variant: 'warning' });
            return;
        }
        const currentAssigned = form.getFieldValue('assignedMeetings') || [];
        setSelectedMeetingIds(currentAssigned);

        setIsAssignModalVisible(true);
    };

    // --- Закрытие модального окна собраний ---
    const closeAssignModal = () => {
        setSearchQuery('');
        setFilters([]);
        setCurrentPage(1);
        setIsAssignModalVisible(false);
    };

    // --- Обработчик подтверждения выбора собраний ---
    const handleAssignConfirm = () => {
        form.setFieldsValue({ assignedMeetings: selectedMeetingIds });
        closeAssignModal();
        enqueueSnackbar(`Выбрано собраний: ${selectedMeetingIds.length}`, { variant: 'success' });
    };

    // --- Фильтрация и пагинация ---
    const getSortedAndFilteredMeetings = () => {
        let filtered = meetings;

        // Фильтрация
        const activeFilters = [...filters];
        if (searchQuery.trim() !== '') {
            activeFilters.push(searchQuery.trim());
        }
        if (activeFilters.length > 0) {
            filtered = meetings.filter(item => {
                const issuerName = issuers[item.issuer] || '';
                const formattedDate = new Date(item.meeting_date).toLocaleDateString('ru-RU').replace(/\//g, '.');
                return activeFilters.every(filter => {
                    const filterLower = filter.toLowerCase();
                    const formattedDateLower = formattedDate.toLowerCase();
                    return (
                        item.meeting_name.toLowerCase().includes(filterLower) ||
                        formattedDateLower.includes(filterLower) ||
                        issuerName.toLowerCase().includes(filterLower)
                    );
                });
            });
        }

        // Сортировка
        filtered.sort((a, b) => {
            let aValue, bValue;
            switch (sortConfig.key) {
                case 'meeting_date':
                    aValue = new Date(a.meeting_date);
                    bValue = new Date(b.meeting_date);
                    break;
                default:
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return filtered;
    };

    // Отдельная функция для пагинации
    const getPaginatedMeetings = (meetingsList) => {
        const startIndex = (currentPage - 1) * pageSize;
        return meetingsList.slice(startIndex, startIndex + pageSize);
    };

    // Общее количество страниц
    const totalPages = Math.ceil(getSortedAndFilteredMeetings().length / pageSize);

    // --- Поиск и фильтры ---
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleEnterSearch = (e) => {
        if (e.key === 'Enter' && searchQuery.trim() !== '') {
            setFilters(prev => [...prev, searchQuery.trim()]);
            setSearchQuery('');
            setCurrentPage(1);
        }
    };

    const removeFilter = (filter) => {
        setFilters(prev => prev.filter(f => f !== filter));
        setCurrentPage(1);
    };

    const renderFilters = () => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
            {filters.map((f, i) => (
                <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px'
                }}>
                    {f}
                    <span
                        onClick={() => removeFilter(f)}
                        style={{ marginLeft: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        ×
                    </span>
                </div>
            ))}
        </div>
    );

    // --- Колонки таблицы ---
    const columns = [
        { title: '№', render: (_, __, index) => (currentPage - 1) * pageSize + index + 1 },
        {
            title: 'Дата собрания',
            render: (record) => new Date(record.meeting_date).toLocaleDateString('ru-RU').replace(/\//g, '.')
        },
        {
            title: 'Имя эмитента',
            render: (record) => issuers[record.issuer] || 'Неизвестно'
        },
        { title: 'Название собрания', dataIndex: 'meeting_name' },
        {
            title: 'Выбрать',
            render: (_, meeting) => (
                <Checkbox
                    checked={selectedMeetingIds.includes(meeting.meeting_id)}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedMeetingIds(prev =>
                            checked
                                ? [...prev, meeting.meeting_id]
                                : prev.filter(id => id !== meeting.meeting_id)
                        );
                    }}
                />
            ),
        },
    ];

    // --- Снятие прав администратора ---
    const handleRevokeAdmin = () => {
        Modal.confirm({
            title: 'Вы хотите забрать права администратора ?',
            content: 'Пользователь потеряет доступ к управлению собраниями.',
            okText: 'Да',
            okType: 'primary',
            cancelText: 'Отмена',
            onOk() {
                form.setFieldsValue({ is_staff: false });
                setSelectedMeetingIds([]);
            },
            onCancel() {
            },
        });
    };

    // --- Назначение администратором ---
    const handleMakeAdmin = () => {
        Modal.confirm({
            title: 'Назначить пользователя ?',
            content: 'Пользователь сможет управлять собраниями.',
            okText: 'Да',
            okType: 'primary',
            cancelText: 'Отмена',
            onOk() {
                form.setFieldsValue({ is_staff: true });
            },
            onCancel() {
            },
        });
    };

    // --- Отправка формы ---
    const handleSubmit = async (values) => {
        const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
        const rawDigits = values.phone?.replace(/\D/g, '') || '';

        let phoneForUsername;

        if (rawDigits.length === 11 && rawDigits.startsWith('7')) {
          phoneForUsername = rawDigits;
        } else if (rawDigits.length === 10) {
          phoneForUsername = '7' + rawDigits;
        } else {
          enqueueSnackbar('Телефон должен содержать 10 цифр после 7', { variant: 'error' });
          return;
        }

        const payload = {
            username: phoneForUsername,
            email: values.email,
            fio: values.fio,
            s_n: values.s_n,
            born: values.born || null,
            is_staff: values.is_staff || false,
        };

        if (values.is_staff && selectedMeetingIds.length > 0) {
            payload.assigned_meeting_ids = selectedMeetingIds;
        }

        if (userToEdit) {
            payload.user_id = userToEdit.id;
        }

        const url = userToEdit
            ? `${API_ADDRESS}update_user/`
            : `${API_ADDRESS}create_user/`;

        setIsSubmitting(true);
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (response.ok) {
                // Сброс формы
                form.resetFields();
                handleModalClose();

                // Если это создание пользователя — запускаем update_connections
                if (!userToEdit) {
                    Swal.fire({
                        title: 'Пользователь успешно создан',
                        html: 'Создание связок с собраниями/выгрузки голосов...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    });

                    try {
                        const userId = data.user_id;
                        await fetch(`${API_ADDRESS}update_connections/${userId}/`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                            },
                        });
                    } catch (error) {
                        console.error('Ошибка при вызове update_connections:', error);
                        // Игнорируем ошибку, продолжаем
                    } finally {
                        Swal.close();
                    }

                    // === Проверка email перед показом модального окна ===
                    if (values.email && values.email.trim() !== '') {
                        Modal.confirm({
                            title: 'Пользователь успешно создан',
                            content: (
                                <div style={{ textAlign: 'left' }}>
                                    Отправить пользователю ссылку на вход ?
                                    <br />
                                    <br />
                                    <span style={{ color: '#595959', fontSize: '14px' }}>
                                        Ссылка будет отправлена на почту: <strong style={{ color: '#1677ff' }}>{values.email}</strong>
                                    </span>
                                </div>
                            ),
                            okText: 'Да',
                            okType: 'primary',
                            cancelText: 'Нет',
                            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                            onOk: async () => {
                                return new Promise(async (resolve, reject) => {
                                    try {
                                        await handleUrlSend({
                                            email: values.email,
                                            id: data.user_id,
                                        });
                                        resolve();
                                    } catch (error) {
                                        reject(error);
                                    }
                                });
                            },
                            onCancel: () => {
                                form.resetFields();
                                handleModalClose();
                                onCreateSuccess?.();
                            },
                        });
                    } else {
                        enqueueSnackbar('Пользователь создан', { variant: 'success' });
                        form.resetFields();
                        handleModalClose();
                        onCreateSuccess?.();
                    }
                } else {
                    enqueueSnackbar('Пользователь обновлён', { variant: 'success' });
                    onUpdateSuccess?.();
                }
            } else {
                enqueueSnackbar(`Ошибка: ${data.error || 'Не удалось сохранить'}`, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Произошла ошибка', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Обновление связок пользователя ---
    const handleUpdateConnections = async () => {
        if (!userToEdit || !userToEdit.id) return;

        Swal.fire({
            title: 'Обновление данных пользователя...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });
        const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
        try {
            const response = await fetch(`${API_ADDRESS}update_connections/${userToEdit.id}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            Swal.close();

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Данные успешно обновлены',
                    confirmButtonText: 'Ок',
                    customClass: {
                        confirmButton: 'confirm-button',
                    },
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Данные не удалось обновить',
                    confirmButtonText: 'Ок',
                    customClass: {
                        confirmButton: 'confirm-button',
                    },
                });
            }
        } catch (error) {
            console.error('Ошибка при обновлении данных:', error);
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Данные не удалось обновить',
                confirmButtonText: 'Ок',
                customClass: {
                    confirmButton: 'confirm-button',
                },
            });
        }
    };

    // --- Отправка ссылки на вход ---
    const sendLoginLink = async (email, userId) => {
    const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
        try {
            const response = await fetch(`${API_ADDRESS}send_login_link/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    email: email,
                    user_id: userId,
                }),
            });
            const data = await response.json();
            if (response.ok) {
                if (data.status === "OK") {
                    if (data.message === "Ссылка уже существует") {
                        enqueueSnackbar('Ссылка уже существует', { variant: 'warning' });
                    } else {
                        enqueueSnackbar('Ссылка успешно отправлена', { variant: 'success' });
                    }
                } else {
                    enqueueSnackbar('Ошибка при отправке ссылки', { variant: 'error' });
                }
            } else {
                enqueueSnackbar('Ошибка при отправке ссылки', { variant: 'error' });
            }
        } catch (error) {
            console.error('Ошибка при отправке ссылки:', error);
            enqueueSnackbar('Произошла ошибка при отправке ссылки', { variant: 'error' });
        }
    };

    const handleUrlSend = async (user) => {
        if (!user || !user.email) {
            enqueueSnackbar('У пользователя не указан email', { variant: 'error' });
            return;
        }
        try {
            await sendLoginLink(user.email, user.id);
        } catch (error) {
            console.error('Ошибка:', error);
            enqueueSnackbar('Произошла ошибка при отправке ссылки', { variant: 'error' });
        } finally {
            Swal.close();
        }
    };

    return (
        <>
            {/* Основное модальное окно */}
            <Modal
                title={userToEdit ? 'Редактирование пользователя' : 'Создание пользователя'}
                open={visible}
                onCancel={handleModalClose}
                footer={null}
                width={600}
            >
                <Form
                    key={userToEdit?.id || 'create'}
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                      fio: userToEdit?.full_name || '',
                      phone: userToEdit ? (
                        userToEdit.username?.replace(/\D/g, '').startsWith('7')
                          ? userToEdit.username.replace(/\D/g, '')
                          : `7${userToEdit.username.replace(/\D/g, '')}`
                      ) : '7',
                      born: userToEdit?.born
                        ? userToEdit.born.split('-').reverse().join('.')
                        : '',
                      s_n: userToEdit?.s_n || '',
                      email: userToEdit?.email || '',
                      is_staff: userToEdit?.is_staff || false,
                      assignedMeetings: userToEdit?.assignedMeetings || [],
                    }}
                >
                    <Form.Item label="Ф.И.О" name="fio" rules={[{ required: true }]}>
                        <Input placeholder="Иванов Иван Иванович" autoComplete="off" />
                    </Form.Item>

                    <Form.Item
                        label="Телефон"
                        name="phone"
                    >
                        <Input
                            ref={phoneInputRef}
                            placeholder="79999999999"
                            autoComplete="off"
                            onInput={(e) => {
                              let value = e.target.value.replace(/[^0-9]/g, '');

                              if (value.startsWith('8') && value.length === 11) {
                                value = '7' + value.slice(1);
                              }

                              if (!value.startsWith('7')) {
                                if (value.length === 0) {
                                  value = '7';
                                } else {
                                  value = '7' + value;
                                }
                              }

                              value = value.slice(0, 11);

                              e.target.value = value;
                            }}
                          />
                    </Form.Item>

                    <Form.Item
                        label="Дата рождения"
                        name="born"
                        getValueFromEvent={(value, dateString) => dateString}
                    >
                        <Flatpickr
                            options={{
                                dateFormat: 'd.m.Y',
                                locale: Russian,
                                maxDate: 'today',
                                allowInput: true,
                                altInput: true,
                                altFormat: 'd.m.Y',
                                onChange: (selectedDates, dateStr) => {
                                    form.setFieldsValue({ born: dateStr });
                                },
                                onReady: (selectedDates, dateStr, instance) => {
                                    // Единый обработчик ввода
                                    const handleInput = (e) => {
                                        let value = e.target.value;

                                        // Сохраняем позицию курсора
                                        const cursorPosition = e.target.selectionStart;

                                        // Разрешаем только цифры и разделители
                                        const cleaned = value.replace(/[^0-9./-]/g, '');
                                        if (cleaned !== value) {
                                            e.target.value = cleaned;
                                            // Восстанавливаем позицию курсора
                                            setTimeout(() => {
                                                e.target.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
                                            }, 0);
                                        }
                                    };

                                    // Обработчик вставки
                                    const handlePaste = (e) => {
                                        e.preventDefault();
                                        const pasted = e.clipboardData.getData('text').trim();

                                        // Проверка формата: ДД.ММ.ГГГГ, ДД/ММ/ГГГГ, ДД-ММ-ГГГГ
                                        const match = pasted.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
                                        if (!match) {
                                            enqueueSnackbar('Введите дату в формате ДД.ММ.ГГГГ', { variant: 'error' });
                                            return;
                                        }

                                        const [_, dayRaw, monthRaw, year] = match;
                                        const day = dayRaw.padStart(2, '0');
                                        const month = monthRaw.padStart(2, '0');

                                        // Проверка диапазонов
                                        if (
                                            parseInt(day) < 1 || parseInt(day) > 31 ||
                                            parseInt(month) < 1 || parseInt(month) > 12 ||
                                            parseInt(year) < 1900 || parseInt(year) > new Date().getFullYear()
                                        ) {
                                            enqueueSnackbar('Неверная дата', { variant: 'error' });
                                            return;
                                        }

                                        const formatted = `${day}.${month}.${year}`;
                                        const dateObj = new Date(`${year}-${month}-${day}`);
                                        if (isNaN(dateObj.getTime()) || dateObj > new Date()) {
                                            enqueueSnackbar('Некорректная дата или дата в будущем', { variant: 'error' });
                                            return;
                                        }

                                        instance.setDate(formatted, false);
                                        form.setFieldsValue({ born: formatted });
                                    };

                                    // Привязываем обработчики к input
                                    instance.input.addEventListener('input', handleInput);
                                    instance.input.addEventListener('paste', handlePaste);

                                    // Очистка при размонтировании
                                    return () => {
                                        instance.input.removeEventListener('input', handleInput);
                                        instance.input.removeEventListener('paste', handlePaste);
                                    };
                                },
                            }}
                            render={({ value, ...props }, ref) => (
                                <input
                                    {...props}
                                    ref={ref}
                                    value={value || ''}
                                    className="main-input4"
                                    style={{ width: '100%' }}
                                    placeholder="дд.мм.гггг"
                                    autoComplete="off"
                                />
                            )}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Серия и номер паспорта"
                        name="s_n"
                    >
                        <Input
                            ref={snInputRef}
                            placeholder="____ ______"
                            autoComplete="off"
                            onInput={(e) => {
                              let value = e.target.value.replace(/[^0-9]/g, '');
                              value = value.slice(0, 10);
                              if (value.length > 4) {
                                e.target.value = value.slice(0, 4) + ' ' + value.slice(4);
                              } else {
                                e.target.value = value;
                              }
                            }}
                          />
                    </Form.Item>

                    <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                        <Input placeholder="example@domain.com" autoComplete="off" />
                    </Form.Item>

                    {(currentUserIsSuperuser || currentUserIsStaff) && (
                        <Form.Item style={{ marginBottom: '24px' }} label="" name="is_staff" valuePropName="value">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {currentUserIsSuperuser && (
                                    <>
                                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                                            Администратор:{' '}
                                            {userToEdit?.is_superuser ? (
                                                <strong style={{ color: '#52c41a' }}>Супер администратор</strong>
                                            ) : (
                                                <strong style={{ color: isStaff ? '#52c41a' : '#ff4d4f' }}>
                                                    {isStaff ? 'Да' : 'Нет'}
                                                </strong>
                                            )}
                                        </div>

                                        {!userToEdit?.is_superuser && (
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                {isStaff ? (
                                                    <>
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            onClick={openAssignModal}
                                                            style={{
                                                                backgroundColor: '#1677ff',
                                                                borderColor: '#1677ff',
                                                                color: 'white',
                                                                fontWeight: 500,
                                                                padding: '4px 15px',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                height: 'auto',
                                                                boxShadow: '0 2px 0 rgba(0, 0, 0, 0.05)',
                                                                transition: 'all 0.3s',
                                                            }}
                                                        >
                                                            Назначить на собрание
                                                        </Button>
                                                        <Button
                                                            danger
                                                            size="small"
                                                            onClick={handleRevokeAdmin}
                                                            style={{
                                                                backgroundColor: '#da1200',
                                                                borderColor: '#da1200',
                                                                color: 'white',
                                                                fontWeight: 500,
                                                                padding: '4px 15px',
                                                                borderRadius: '6px',
                                                                fontSize: '14px',
                                                                height: 'auto',
                                                                boxShadow: '0 2px 0 rgba(0, 0, 0, 0.05)',
                                                                transition: 'all 0.3s',
                                                            }}
                                                        >
                                                            Снять админ. права
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onClick={handleMakeAdmin}
                                                        style={{
                                                            backgroundColor: '#1677ff',
                                                            borderColor: '#1677ff',
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            padding: '4px 15px',
                                                            borderRadius: '6px',
                                                            fontSize: '14px',
                                                            height: 'auto',
                                                            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.05)',
                                                            transition: 'all 0.3s',
                                                        }}
                                                    >
                                                        Назначить администратором
                                                    </Button>
                                                )}
                                            </div>
                                        )}

                                        {userToEdit && (
                                            <div style={{ borderTop: '1px dashed #d9d9d9', marginTop: '12px', paddingTop: '12px' }}>
                                                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                                                    Обновление данных:
                                                </div>
                                                <div style={{ display: 'inline-block' }}>
                                                    <Button
                                                        type="dashed"
                                                        size="small"
                                                        onClick={handleUpdateConnections}
                                                        style={{
                                                            backgroundColor: '#1677ff',
                                                            borderColor: '#1677ff',
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            padding: '4px 15px',
                                                            borderRadius: '6px',
                                                            fontSize: '14px',
                                                            height: 'auto',
                                                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                                            transition: 'all 0.3s',
                                                        }}
                                                    >
                                                        Обновить
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </Form.Item>
                    )}

                    <div style={{ textAlign: 'right', marginTop: '20px' }}>
                        <Button onClick={handleModalClose}>Отмена</Button>
                        <Button type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                                style={{marginLeft: '8px'}}
                        >
                            {userToEdit ? 'Сохранить' : 'Создать'}
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* Модальное окно выбора собраний */}
            <Modal
                title="Доступные собрания"
                open={isAssignModalVisible}
                onCancel={closeAssignModal}
                footer={[
                    <Button key="cancel" onClick={closeAssignModal}>
                        Отмена
                    </Button>,
                    <Button key="ok" type="primary" onClick={handleAssignConfirm}>
                        Ок
                    </Button>,
                ]}
                width="70%"
                style={{ top: 20 }}
            >
                <div style={{ marginBottom: '10px' }}>
                    {/* Строка поиска */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <Input
                            placeholder="Поиск..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleEnterSearch}
                            style={{ width: '300px' }}
                        />
                        <Tooltip title="В строке можно использовать последовательную фильтрацию по разным условиям. Нажмите Enter после ввода данных для создания одного условия">
                            <InfoCircleOutlined style={{ fontSize: '16px', color: '#999', cursor: 'pointer' }} />
                        </Tooltip>
                    </div>

                    {/* Фильтры */}
                    {renderFilters()}
                </div>

                {/* Обёртка с внешним скроллом */}
                <div
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: '1px solid #e8e8e8',
                        borderRadius: '4px',
                        position: 'relative',
                    }}
                >
                    {/* Фиксированные заголовки */}
                    <div
                        style={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: '#f5f5f5',
                            zIndex: 1,
                            borderBottom: '1px solid #e8e8e8',
                            display: 'grid',
                            gridTemplateColumns: '60px 1fr 1.5fr 2fr',
                            padding: '8px 16px',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#000',
                        }}
                    >
                        <div>№</div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSortConfig(prev => ({
                                    key: 'meeting_date',
                                    direction: prev.key === 'meeting_date' && prev.direction === 'desc' ? 'asc' : 'desc'
                                }));
                                setCurrentPage(1); // Сброс на первую страницу при смене сортировки
                            }}
                        >
                            Дата собрания
                            <span style={{marginLeft: '8px', fontSize: '10px'}}>
                                {sortConfig.key === 'meeting_date' ? (
                                    sortConfig.direction === 'desc' ? (
                                        <span style={{color: '#007bff', fontWeight: 'bold'}}>↓</span>
                                    ) : (
                                        <span style={{color: '#007bff', fontWeight: 'bold'}}>↑</span>
                                    )
                                ) : (
                                    <span style={{color: '#ccc', fontWeight: 'bold'}}>⇅</span>
                                )}
                            </span>
                        </div>
                        <div>Имя эмитента</div>
                        <div>Название собрания</div>
                    </div>

                    {/* Тело таблицы */}
                    {getPaginatedMeetings(getSortedAndFilteredMeetings()).map((meeting, index) => {
                        const globalIndex = (currentPage - 1) * pageSize + index + 1;
                        const issuerName = issuers[meeting.issuer] || 'Неизвестно';
                        const formattedDate = new Date(meeting.meeting_date).toLocaleDateString('ru-RU').replace(/\//g, '.');

                        const isSelected = selectedMeetingIds.includes(meeting.meeting_id);

                        return (
                            <div
                                key={meeting.meeting_id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '60px 1fr 1.5fr 2fr',
                                    padding: '10px 16px',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: '14px',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#e6f7ff' : 'white',
                                    borderLeft: isSelected ? '4px solid #1890ff' : '4px solid transparent',
                                    transition: 'all 0.2s ease',
                                }}
                                onClick={() => {
                                    // При клике: если уже выбрано — убираем, если нет — добавляем
                                    setSelectedMeetingIds(prev =>
                                        prev.includes(meeting.meeting_id)
                                            ? prev.filter(id => id !== meeting.meeting_id)
                                            : [...prev, meeting.meeting_id]
                                    );
                                }}
                            >
                                <div>{globalIndex}</div>
                                <div>{formattedDate}</div>
                                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {issuerName}
                                </div>
                                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {meeting.meeting_name}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Пагинация */}
                {totalPages > 1 && (
                    (() => {
                        const groupSize = 10;
                        const currentGroup = Math.ceil(currentPage / groupSize);
                        const startPage = (currentGroup - 1) * groupSize + 1;
                        const endPage = Math.min(currentGroup * groupSize, totalPages);

                        const goToPage = (page) => {
                            if (page >= 1 && page <= totalPages) {
                                this.setState({
                                    currentPage: page,
                                    selectedIndex: null,
                                    selectedUser: null,
                                });
                            }
                        };

                        const goToPrevGroup = () => {
                            const prevStart = startPage - groupSize;
                            const newPage = prevStart >= 1 ? prevStart : 1;
                            this.setState({
                                currentPage: newPage,
                                selectedIndex: null,
                                selectedUser: null,
                            });
                        };

                        const goToNextGroup = () => {
                            const nextStart = startPage + groupSize;
                            if (nextStart <= totalPages) {
                                this.setState({
                                    currentPage: nextStart,
                                    selectedIndex: null,
                                    selectedUser: null,
                                });
                            }
                        };

                        return (
                            <div style={{ width: '100%' }}>
                                {/* Первая строка — номера страниц */}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-end',
                                        gap: '4px',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                        const page = startPage + i;
                                        return (
                                            <Button
                                                key={page}
                                                type={currentPage === page ? 'primary' : 'default'}
                                                size="small"
                                                onClick={() => goToPage(page)}
                                                style={{
                                                    minWidth: '32px',
                                                    padding: '0 6px',
                                                    fontSize: '12px',
                                                    marginTop: '8px',
                                                }}
                                            >
                                                {page}
                                            </Button>
                                        );
                                    })}
                                </div>

                                {/* Вторая строка — стрелки перелистывания */}
                                {(currentGroup > 1 || currentGroup * groupSize < totalPages) && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: '4px',
                                        }}
                                    >
                                        {currentGroup > 1 && (
                                            <Button
                                                type="default"
                                                size="small"
                                                onClick={goToPrevGroup}
                                                icon={<CaretLeftOutlined style={{ fontSize: '12px' }} />}
                                                style={{ fontWeight: 'bold' }}
                                            />
                                        )}
                                        {currentGroup * groupSize < totalPages && (
                                            <Button
                                                type="default"
                                                size="small"
                                                onClick={goToNextGroup}
                                                icon={<CaretRightOutlined style={{ fontSize: '12px' }} />}
                                                style={{ fontWeight: 'bold' }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()
                )}

                <div style={{ marginTop: '10px', fontSize: '13px', color: '#666', textAlign: 'right' }}>
                    Выбрано: <b>{selectedMeetingIds.length}</b> собраний
                </div>
            </Modal>
        </>
    );
};

export default UserFormModal;